
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Bot, Users, Zap, Target, DollarSign, ArrowRight, BarChart2, Layout, Cpu, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type TabKey = 'gantt' | 'kanban' | 'ia' | 'automatizacion' | 'equipos';

const TABS: { key: TabKey; label: string; color: string; bg: string }[] = [
  { key: 'gantt', label: 'Gantt', color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
  { key: 'kanban', label: 'Kanban', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' },
  { key: 'ia', label: 'IA Predictiva', color: '#EC4899', bg: 'rgba(236,72,153,0.12)' },
  { key: 'automatizacion', label: 'Automatización', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  { key: 'equipos', label: 'Equipos en Tiempo Real', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
];

const TAB_CONTENT: Record<TabKey, {
  icon: React.FC<any>;
  iconColor: string;
  title: string;
  description: string;
  previewItems: string[];
  previewColor: string;
}> = {
  gantt: {
    icon: BarChart2,
    iconColor: '#7C3AED',
    title: 'Diagrama de Gantt interactivo',
    description: 'Visualiza el cronograma completo de tus proyectos con dependencias, hitos y recursos asignados. Arrastra y suelta para ajustar fechas al instante.',
    previewItems: ['Dependencias automáticas', 'Vista de recursos', 'Milestones visuales', 'Exporta en PDF / PNG'],
    previewColor: '#7C3AED',
  },
  kanban: {
    icon: Layout,
    iconColor: '#06B6D4',
    title: 'Tablero Kanban flexible',
    description: 'Organiza tu flujo de trabajo en columnas personalizables. Filtra por asignado, prioridad o etiqueta con un solo clic.',
    previewItems: ['Columnas ilimitadas', 'WIP limits', 'Filtros avanzados', 'Drag & drop nativo'],
    previewColor: '#06B6D4',
  },
  ia: {
    icon: Brain,
    iconColor: '#EC4899',
    title: 'IA Predictiva incluida',
    description: 'Detecta tareas en riesgo antes de que ocurran retrasos. Nuestra IA analiza patrones históricos y alerta al equipo proactivamente.',
    previewItems: ['Alertas tempranas de riesgo', 'Estimación inteligente', 'Sugerencias de optimización', 'Aprendizaje continuo'],
    previewColor: '#EC4899',
  },
  automatizacion: {
    icon: Zap,
    iconColor: '#F59E0B',
    title: 'Automatización sin código',
    description: 'Crea flujos de trabajo automáticos que eliminan tareas repetitivas. Conecta triggers con acciones en segundos, sin programar.',
    previewItems: ['Triggers personalizados', 'Acciones en cascada', 'Integraciones con Slack/Jira', 'Templates listos para usar'],
    previewColor: '#F59E0B',
  },
  equipos: {
    icon: Users,
    iconColor: '#10B981',
    title: 'Colaboración en tiempo real',
    description: 'Trabaja con tu equipo simultáneamente. Los cambios se sincronizan al instante para que todos estén siempre actualizados.',
    previewItems: ['Cambios en tiempo real', 'Comentarios en tareas', 'Menciones @usuario', 'Historial de actividad'],
    previewColor: '#10B981',
  },
};

// Minimal visual preview component
const FeaturePreview = ({ tabKey }: { tabKey: TabKey }) => {
  const c = TAB_CONTENT[tabKey];
  const color = c.previewColor;
  return (
    <div
      className="flex flex-col gap-2 p-4 rounded-xl"
      style={{ background: '#1A1825', minHeight: '180px' }}
    >
      {c.previewItems.map((item, i) => (
        <div
          key={item}
          className="flex items-center gap-3 px-3 py-2 rounded-lg"
          style={{
            background: i === 0 ? `${color}18` : 'rgba(255,255,255,0.03)',
            border: i === 0 ? `1px solid ${color}30` : '1px solid transparent',
          }}
        >
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: i === 0 ? color : '#475569' }} />
          <span style={{ color: i === 0 ? '#F1F5F9' : '#64748B', fontSize: '0.75rem', fontFamily: 'Manrope, sans-serif', fontWeight: i === 0 ? 700 : 400 }}>
            {item}
          </span>
          {i === 0 && (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: `${color}20`, color }}>
              NUEVO
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const Features = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabKey>('gantt');
  const active = TAB_CONTENT[activeTab];
  const ActiveIcon = active.icon;

  return (
    <section id="features" className="py-24" style={{ background: '#FAFBFF' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div
            className="pm-pill mb-5 inline-flex"
            style={{ background: 'rgba(124,58,237,0.08)', color: '#7C3AED', border: '1px solid rgba(124,58,237,0.2)' }}
          >
            FUNCIONALIDADES
          </div>
          <h2
            className="mb-4"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: '#0F0E17',
              lineHeight: 1.15,
            }}
          >
            Todo lo que tu equipo necesita,{' '}
            <em className="pm-gradient-text" style={{ fontStyle: 'italic' }}>sin costo</em>
          </h2>
          <p style={{ color: '#475569', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto', fontFamily: 'Manrope, sans-serif' }}>
            {t('features.subtitle')}
          </p>
        </div>

        {/* Tab pills */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="pm-pill transition-all"
                style={{
                  background: isActive ? tab.bg : 'white',
                  color: isActive ? tab.color : '#475569',
                  border: isActive ? `1.5px solid ${tab.color}40` : '1.5px solid #E2E8F0',
                  padding: '8px 20px',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: isActive ? 700 : 500,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Feature panel */}
        <div
          className="rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2"
          style={{ background: '#0F0E17', border: '1.5px solid #1E1B30', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
        >
          {/* Left: text */}
          <div className="p-8 lg:p-10 flex flex-col justify-center">
            <div
              className="flex items-center justify-center mb-6"
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '14px',
                background: `${active.iconColor}18`,
                border: `1.5px solid ${active.iconColor}30`,
              }}
            >
              <ActiveIcon style={{ width: '26px', height: '26px', color: active.iconColor }} />
            </div>
            <h3
              className="mb-4"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '1.55rem',
                fontWeight: 800,
                color: 'white',
                letterSpacing: '-0.03em',
                lineHeight: 1.2,
              }}
            >
              {active.title}
            </h3>
            <p
              className="mb-8"
              style={{ color: '#94A3B8', fontSize: '0.95rem', fontFamily: 'Manrope, sans-serif', lineHeight: 1.7 }}
            >
              {active.description}
            </p>
            <button
              onClick={() => navigate('/features')}
              className="inline-flex items-center gap-2 font-semibold transition-all"
              style={{ color: active.iconColor, fontSize: '0.9rem', fontFamily: 'Manrope, sans-serif', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Aprende más <ArrowRight size={16} />
            </button>
          </div>

          {/* Right: preview */}
          <div
            className="p-6 flex items-center"
            style={{ background: '#1F1B2E', borderLeft: '1px solid #2D2840' }}
          >
            <div className="w-full">
              <FeaturePreview tabKey={activeTab} />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/auth')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-white transition-all"
            style={{
              background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
              fontSize: '1rem',
              boxShadow: '0 8px 24px rgba(124,58,237,0.35)',
              fontFamily: 'Manrope, sans-serif',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {t('features.ctaButton')} <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Features;
